import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/** アプリ共通の表示・日付境界（日本） */
export const APP_TIMEZONE = "Asia/Tokyo";

/** ISO の Z / 数値オフセット付きなら true（タイムゾーンなしの素の DB 文字列は false） */
function hasExplicitTimeZone(s) {
  return /\d[zZ]$|[+\-]\d{2}:\d{2}$|[+\-]\d{4}$/.test(s);
}

/** オフセットなしの日時文字列を Asia/Tokyo の壁時計として解釈（サーバが JST で保存している前提） */
const NAIVE_TOKYO_FORMATS = [
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY/MM/DD HH:mm:ss",
  "YYYY-MM-DD",
  "YYYY/MM/DD"
];

function parseNaiveAsTokyo(s) {
  for (let i = 0; i < NAIVE_TOKYO_FORMATS.length; i++) {
    const d = dayjs.tz(s, NAIVE_TOKYO_FORMATS[i], APP_TIMEZONE);
    if (d.isValid()) return d;
  }
  const loose = dayjs.tz(s, APP_TIMEZONE);
  return loose.isValid() ? loose : null;
}

/**
 * DB 等に保存された日時を Asia/Tokyo で表示する（サーバ・DB が JST 素の DATETIME 等である前提）。
 * - Z や ±オフセット付きは「その瞬間」として解釈し、表示は東京の壁時計に変換
 * - タイムゾーンなしの文字列は **Asia/Tokyo の壁時計**として解釈（UTC と誤認しない）
 */
export function formatUtcStoredAsTokyo(val, fmt = "YYYY/MM/DD HH:mm") {
  if (val == null || val === "") return "";
  if (typeof val === "number") {
    const ms = val > 0 && val < 1e12 ? val * 1000 : val;
    return dayjs(ms).tz(APP_TIMEZONE).format(fmt);
  }
  if (val instanceof Date) {
    return dayjs(val).tz(APP_TIMEZONE).format(fmt);
  }
  const s = String(val).trim();
  if (!s) return "";
  if (hasExplicitTimeZone(s)) {
    const i = dayjs(s);
    return i.isValid() ? i.tz(APP_TIMEZONE).format(fmt) : "";
  }
  const m = parseNaiveAsTokyo(s);
  if (m) return m.format(fmt);
  return dayjs(s).tz(APP_TIMEZONE).format(fmt);
}

/**
 * 保存値を同一「瞬間」として解釈した dayjs（diff / 比較用）。素文字列は Tokyo 壁時計として解釈。
 */
export function parseUtcStoredToInstant(val) {
  if (val == null || val === "") {
    return dayjs("");
  }
  if (typeof val === "number") {
    const ms = val > 0 && val < 1e12 ? val * 1000 : val;
    return dayjs(ms);
  }
  if (val instanceof Date) {
    return dayjs(val);
  }
  const s = String(val).trim();
  if (!s) {
    return dayjs("");
  }
  if (hasExplicitTimeZone(s)) {
    const i = dayjs(s);
    return i.isValid() ? i : dayjs("");
  }
  const m = parseNaiveAsTokyo(s);
  return m || dayjs(s);
}

/** ログ API 用の日付範囲（走行ルートと同様の …Z 形式・日単位） */
export function logQueryRangeFromDates(dateFrom, dateTo) {
  return {
    from: `${dateFrom}T00:00:00Z`,
    to: `${dateTo}T23:59:59Z`
  };
}

/** 本日（Asia/Tokyo）1日分 */
export function logQueryRangeToday() {
  const d = dayjs().tz(APP_TIMEZONE).format("YYYY-MM-DD");
  return logQueryRangeFromDates(d, d);
}

export function parseLogApiResponse(response) {
  if (response == null || response === "") {
    return [];
  }
  return typeof response === "string" ? JSON.parse(response) : response;
}

export default dayjs;
